// OAuth2 认证服务
// 负责管理 access token 和 refresh token 的生命周期
import { BizAppConfig } from 'config/application.manifest';
import { refreshToken as refreshAccessToken } from './bff';

// OAuth2 Token 信息接口
export interface OAuth2TokenInfo {
  access_token: string; // 访问令牌
  refresh_token?: string; // 刷新令牌（可选）
  expires_in: number; // 过期时间（秒）
  token_type: string; // 令牌类型
  id_token?: string; // ID 令牌（可选）
}

// Token 存储键名 - 与现有系统保持一致
const TOKEN_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  EXPIRES_AT: 'token_expires_at',
  USER: 'user',
  CURRENT_TENANT_UUID: 'currentTenantUuid',
} as const;

const TOKEN_EXPIRY_BUFFER_MS = 5 * 60 * 1000;
const SSO_REDIRECT_LOCK_KEY = 'sso_redirect_lock';
const SSO_REDIRECT_LOCK_TTL_MS = 10_000;
const PENDING_OAUTH_NAVIGATION_KEY = 'pending_oauth_navigation';
const OAUTH_NAVIGATION_TTL_MS = 10 * 60 * 1000;
const PENDING_APPLICATION_ACCESS_RETURN_TO_KEY =
  'pending_application_access_return_to';

type PendingOAuthNavigation = {
  state: string;
  returnTo: string;
  createdAt: number;
};

type PendingApplicationAccessReturnTo = {
  returnTo: string;
  createdAt: number;
};

let refreshTokenPromise: Promise<OAuth2TokenInfo | null> | null = null;
let ssoRedirectInFlight = false;

const getSSORedirectLockTimestamp = (): number | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const rawValue = sessionStorage.getItem(SSO_REDIRECT_LOCK_KEY);
  if (!rawValue) {
    return null;
  }

  const timestamp = Number(rawValue);
  return Number.isFinite(timestamp) ? timestamp : null;
};

const setSSORedirectLock = (): void => {
  ssoRedirectInFlight = true;
  if (typeof window !== 'undefined') {
    sessionStorage.setItem(SSO_REDIRECT_LOCK_KEY, String(Date.now()));
  }
};

export const clearSSORedirectLock = (): void => {
  ssoRedirectInFlight = false;
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem(SSO_REDIRECT_LOCK_KEY);
  }
};

const hasRecentSSORedirectLock = (): boolean => {
  if (ssoRedirectInFlight) {
    return true;
  }

  const timestamp = getSSORedirectLockTimestamp();
  if (!timestamp) {
    return false;
  }

  if (Date.now() - timestamp <= SSO_REDIRECT_LOCK_TTL_MS) {
    return true;
  }

  clearSSORedirectLock();
  return false;
};

const getCurrentApplicationPath = (): string => {
  const { pathname, search, hash } = window.location;
  return `${pathname}${search}${hash}`;
};

const normalizeInternalReturnTo = (returnTo: string): string => {
  if (!returnTo.startsWith('/') || returnTo.startsWith('//')) {
    throw new Error('OAuth return URL must be an internal path');
  }

  const returnUrl = new URL(returnTo, window.location.origin);
  if (returnUrl.origin !== window.location.origin) {
    throw new Error('OAuth return URL must use the application origin');
  }

  return `${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`;
};

const isPendingOAuthNavigation = (
  value: unknown,
): value is PendingOAuthNavigation => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  return (
    'state' in value &&
    typeof value.state === 'string' &&
    'returnTo' in value &&
    typeof value.returnTo === 'string' &&
    'createdAt' in value &&
    typeof value.createdAt === 'number'
  );
};

export const createOAuthNavigation = (returnTo: string): string => {
  const navigation: PendingOAuthNavigation = {
    state: crypto.randomUUID(),
    returnTo: normalizeInternalReturnTo(returnTo),
    createdAt: Date.now(),
  };

  sessionStorage.setItem(
    PENDING_OAUTH_NAVIGATION_KEY,
    JSON.stringify(navigation),
  );
  return navigation.state;
};

export const consumeOAuthNavigation = (state: string | null): string | null => {
  const rawNavigation = sessionStorage.getItem(PENDING_OAUTH_NAVIGATION_KEY);
  sessionStorage.removeItem(PENDING_OAUTH_NAVIGATION_KEY);

  if (!state || !rawNavigation) {
    return null;
  }

  let parsedNavigation: unknown;
  try {
    parsedNavigation = JSON.parse(rawNavigation);
  } catch {
    return null;
  }

  if (
    !isPendingOAuthNavigation(parsedNavigation) ||
    parsedNavigation.state !== state ||
    Date.now() - parsedNavigation.createdAt > OAUTH_NAVIGATION_TTL_MS
  ) {
    return null;
  }

  try {
    return normalizeInternalReturnTo(parsedNavigation.returnTo);
  } catch {
    return null;
  }
};

export const saveApplicationAccessReturnTo = (returnTo: string): void => {
  const pendingReturnTo: PendingApplicationAccessReturnTo = {
    returnTo: normalizeInternalReturnTo(returnTo),
    createdAt: Date.now(),
  };

  sessionStorage.setItem(
    PENDING_APPLICATION_ACCESS_RETURN_TO_KEY,
    JSON.stringify(pendingReturnTo),
  );
};

export const consumeApplicationAccessReturnTo = (): string | null => {
  const rawReturnTo = sessionStorage.getItem(
    PENDING_APPLICATION_ACCESS_RETURN_TO_KEY,
  );
  sessionStorage.removeItem(PENDING_APPLICATION_ACCESS_RETURN_TO_KEY);

  if (!rawReturnTo) {
    return null;
  }

  let parsedReturnTo: unknown;
  try {
    parsedReturnTo = JSON.parse(rawReturnTo);
  } catch {
    return null;
  }

  if (
    typeof parsedReturnTo !== 'object' ||
    parsedReturnTo === null ||
    !('returnTo' in parsedReturnTo) ||
    typeof parsedReturnTo.returnTo !== 'string' ||
    !('createdAt' in parsedReturnTo) ||
    typeof parsedReturnTo.createdAt !== 'number' ||
    Date.now() - parsedReturnTo.createdAt > OAUTH_NAVIGATION_TTL_MS
  ) {
    return null;
  }

  try {
    return normalizeInternalReturnTo(parsedReturnTo.returnTo);
  } catch {
    return null;
  }
};

export const clearApplicationAccessReturnTo = (): void => {
  sessionStorage.removeItem(PENDING_APPLICATION_ACCESS_RETURN_TO_KEY);
};

/**
 * 保存 OAuth2 token 信息到本地存储
 * @param tokenInfo OAuth2 token 信息
 */
export const saveOAuth2Tokens = (tokenInfo: OAuth2TokenInfo): void => {
  try {
    // 保存 access token
    localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, tokenInfo.access_token);
    // 保存 refresh token（如果存在）
    if (tokenInfo.refresh_token) {
      localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, tokenInfo.refresh_token);
    }
    // 计算并保存过期时间戳
    const expiresAt = Date.now() + tokenInfo.expires_in * 1000;
    localStorage.setItem(TOKEN_KEYS.EXPIRES_AT, expiresAt.toString());
    console.log('🔐 OAuth2 Token 信息已保存');
  } catch (error) {
    console.error('❌ 保存 OAuth2 Token 失败:', error);
  }
};

/**
 * 获取当前 access token
 * @returns access token 或 null
 */
export const getAccessToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
};

/**
 * 获取当前 refresh token
 * @returns refresh token 或 null
 */
export const getRefreshToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);
};

/**
 * 获取 token 过期时间戳
 * @returns 过期时间戳或 null
 */
export const getTokenExpiresAt = (): number | null => {
  const expiresAt = localStorage.getItem(TOKEN_KEYS.EXPIRES_AT);
  return expiresAt ? parseInt(expiresAt, 10) : null;
};

/**
 * 检查 token 是否已过期
 * @returns 是否已过期
 */
export const isTokenExpired = (): boolean => {
  const expiresAt = getTokenExpiresAt();
  if (!expiresAt) return true;
  return Date.now() >= expiresAt;
};

/**
 * 检查 token 是否即将过期（提前5分钟）
 * @returns 是否即将过期
 */
export const isTokenExpiringSoon = (): boolean => {
  const expiresAt = getTokenExpiresAt();
  if (!expiresAt) return true;
  return expiresAt - Date.now() < TOKEN_EXPIRY_BUFFER_MS;
};

/**
 * 检查是否仍保留会话信息（access token 或 refresh token）
 * @returns 是否有可用会话
 */
export const hasOAuth2Session = (): boolean => {
  return Boolean(getAccessToken() || getRefreshToken());
};

/**
 * 检查是否已认证（access token 有效）或可通过 refresh token 无感续期
 * @returns 是否已认证/可续期
 */
export const isAuthenticated = (): boolean => {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();

  if (accessToken && !isTokenExpired()) {
    return true;
  }

  return Boolean(refreshToken);
};

const refreshTokensWithLock = async (): Promise<OAuth2TokenInfo | null> => {
  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  const refreshTokenValue = getRefreshToken();
  if (!refreshTokenValue) {
    return null;
  }

  refreshTokenPromise = (async () => {
    try {
      console.log('🔄 开始自动刷新 OAuth2 Token...');
      const response = await refreshAccessToken(
        refreshTokenValue,
        BizAppConfig.appSsoClientId as string,
        {
          skipErrorHandler: true,
        },
      );

      if (!response?.access_token) {
        throw new Error('refresh token response missing access_token');
      }

      saveOAuth2Tokens(response);
      console.log('✅ OAuth2 Token 自动刷新成功');
      return response;
    } catch (error) {
      console.error('❌ OAuth2 Token 自动刷新失败:', error);
      clearOAuth2Tokens();
      return null;
    } finally {
      refreshTokenPromise = null;
    }
  })();

  return refreshTokenPromise;
};

/**
 * 重定向到 SSO 登录页
 */
export function redirectToSSO(): void;
export function redirectToSSO(returnTo: string): void;
export function redirectToSSO(returnTo?: string): void {
  if (typeof window === 'undefined') return;
  if (hasRecentSSORedirectLock()) return;

  const clientId = BizAppConfig.appSsoClientId ?? '';
  const redirectUri = `${window.location.origin}/callback`;
  const authorizeUrl = BizAppConfig.appSsoAuthorizeUrl?.trim();

  if (authorizeUrl) {
    const authorizeEndpoint = new URL(authorizeUrl, window.location.origin);

    if (clientId && !authorizeEndpoint.searchParams.has('client_id')) {
      authorizeEndpoint.searchParams.set('client_id', clientId);
    }
    if (!authorizeEndpoint.searchParams.has('response_type')) {
      authorizeEndpoint.searchParams.set('response_type', 'code');
    }
    if (!authorizeEndpoint.searchParams.has('redirect_uri')) {
      authorizeEndpoint.searchParams.set('redirect_uri', redirectUri);
    }
    const state = createOAuthNavigation(
      returnTo === undefined ? getCurrentApplicationPath() : returnTo,
    );
    authorizeEndpoint.searchParams.set('state', state);

    setSSORedirectLock();
    console.log('🔄 正在重定向到 SSO 授权页...');
    window.location.href = authorizeEndpoint.toString();
    return;
  }

  console.warn('⚠️ 未配置 SSO 授权地址或登录地址');
}

/**
 * 确保获取到可用 access token（必要时使用 refresh token 单飞刷新）
 * @returns 可用 access token，失败返回 null
 */
export const ensureValidAccessToken = async (): Promise<string | null> => {
  const accessToken = getAccessToken();

  if (accessToken && !isTokenExpiringSoon()) {
    return accessToken;
  }

  const refreshTokenValue = getRefreshToken();
  // 如果没有 access token 且没有 refresh token，说明根本没登录，尝试 SSO 重定向
  if (!accessToken && !refreshTokenValue) {
    // 排除掉已经是在 callback 的路径，防止无限循环
    // 同时也排除登录页和带有 error 参数的情况，避免在登录失败后自动跳转导致死循环

    if (typeof window !== 'undefined') {
      const { pathname, search } = window.location;
      const isPublicPath =
        pathname.includes('/callback') ||
        pathname.includes('/user/login') ||
        pathname === '/portal-site' ||
        pathname.startsWith('/portal-site/');
      const hasError = search.includes('error=');

      if (!isPublicPath && !hasError) {
        redirectToSSO();
        // 返回一个永远不会 resolve 的 promise 或者 null，因为页面即将跳转
        return null;
      }
    }
  }

  const refreshedTokenInfo = await refreshTokensWithLock();
  return refreshedTokenInfo?.access_token || getAccessToken();
};

/**
 * 自动刷新 token（如果需要）
 * @returns 是否刷新成功
 */
export const autoRefreshToken = async (): Promise<boolean> => {
  // 检查是否需要刷新
  if (!isTokenExpiringSoon() && !isTokenExpired()) {
    return true; // 不需要刷新
  }

  const token = await ensureValidAccessToken();
  return Boolean(token);
};

/**
 * 清除所有 OAuth2 token 信息
 */
export const clearOAuth2Tokens = (): void => {
  try {
    localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.EXPIRES_AT);
    localStorage.removeItem(TOKEN_KEYS.USER);
    localStorage.removeItem(TOKEN_KEYS.CURRENT_TENANT_UUID);
    refreshTokenPromise = null;
    // 清除定时刷新任务
    clearAutoRefresh();
    console.log('🔐 OAuth2 Token 信息已清除');
  } catch (error) {
    console.error('❌ 清除 OAuth2 Token 失败:', error);
  }
};

/**
 * 设置定时刷新任务
 * 在 token 即将过期前自动刷新
 */
export const setupAutoRefresh = (): void => {
  // 清除可能存在的旧定时器
  if (typeof window !== 'undefined' && window.oauth2RefreshTimer) {
    clearInterval(window.oauth2RefreshTimer);
  }

  // 每分钟检查一次 token 状态
  if (typeof window !== 'undefined') {
    window.oauth2RefreshTimer = setInterval(async () => {
      if (isTokenExpiringSoon()) {
        console.log('🔄 检测到 Token 即将过期，自动刷新...');
        await autoRefreshToken();
      }
    }, 1000 * 60) as unknown as number; // 每分钟检查一次
    console.log('🔄 OAuth2 Token 自动刷新任务已启动');
  }
};

/**
 * 清除定时刷新任务
 */
export const clearAutoRefresh = (): void => {
  if (typeof window !== 'undefined' && window.oauth2RefreshTimer) {
    clearInterval(window.oauth2RefreshTimer);
    window.oauth2RefreshTimer = undefined;
    console.log('🔄 OAuth2 Token 自动刷新任务已停止');
  }
};

// 扩展 Window 接口以支持定时器
declare global {
  interface Window {
    oauth2RefreshTimer?: number;
  }
}
